import { z } from 'zod';
/**
 * ehr#437 part 4 (PROGRESS_NOTE_10-14-V81 Quick add, V50): dot phrases. Typing ".name" in a note
 * box offers the phrase; {client}, {date}, {length} and {last plan} fill in, and each __ is a
 * blank the cursor goes to. The practice's phrases (admins and managers edit them) and each
 * person's own; theirs wins on the same name.
 */
export const DOT_NAME_RE = /^[a-z][a-z0-9-]{1,19}$/;
export const DOT_PHRASES_MAX = 100;
export const dotPhraseSchema = z.object({
    name: z
        .string()
        .regex(DOT_NAME_RE, 'A name is 2 to 20 lowercase letters, numbers or dashes, starting with a letter.'),
    text: z.string().trim().min(1).max(2000),
});
export const dotPhraseListSchema = z.object({
    phrases: z
        .array(dotPhraseSchema)
        .max(DOT_PHRASES_MAX)
        .refine((l) => new Set(l.map((p) => p.name)).size === l.length, {
        message: 'Each dot phrase needs its own name.',
    }),
});
// ── ehr#571 (PROGRESS_NOTE_10-20-V141 openAddPhrase): "+ Add phrase" under a note box ──────────
// A person adds their own phrase pill to one box, for one client or for all their clients. Kept in
// users."notePrefs".boxPhrases: { all: { box: [phrase] }, clients: { contactId: { box: [phrase] } } }.
export const BOX_PHRASES_MAX = 30;
const boxKey = z.string().regex(/^[a-z][a-z0-9_]{1,31}$/);
export const boxPhraseAddSchema = z.object({
    box: boxKey,
    phrase: z.string().trim().min(1).max(200),
    /** Set: this client only. Left out: all clients. */
    contactId: z.string().min(1).max(64).optional(),
});
const phraseLists = (v) => {
    if (!v || typeof v !== 'object' || Array.isArray(v))
        return {};
    const out = {};
    for (const [k, l] of Object.entries(v)) {
        if (!boxKey.safeParse(k).success || !Array.isArray(l))
            continue;
        const ok = l.filter((p) => typeof p === 'string' && !!p.trim());
        if (ok.length)
            out[k] = [...new Set(ok)].slice(0, BOX_PHRASES_MAX);
    }
    return out;
};
/** The stored box phrases, keeping only well-formed lists. */
export function readBoxPhrases(v) {
    const o = v && typeof v === 'object' && !Array.isArray(v) ? v : {};
    const clients = {};
    const c = o.clients && typeof o.clients === 'object' ? o.clients : {};
    for (const [id, lists] of Object.entries(c)) {
        const l = phraseLists(lists);
        if (Object.keys(l).length)
            clients[id] = l;
    }
    return { all: phraseLists(o.all), clients };
}
/** One phrase added: to this client's list for the box, or to the all-clients list. Unchanged when already there. */
export function addBoxPhrase(bp, input) {
    const phrase = input.phrase.replace(/\s+/g, ' ').trim();
    const add = (l) => l?.includes(phrase) ? l : [...(l ?? []), phrase].slice(-BOX_PHRASES_MAX);
    if (!input.contactId)
        return { ...bp, all: { ...bp.all, [input.box]: add(bp.all[input.box]) } };
    const mine = bp.clients[input.contactId] ?? {};
    return {
        ...bp,
        clients: { ...bp.clients, [input.contactId]: { ...mine, [input.box]: add(mine[input.box]) } },
    };
}
/** The pills a box offers from these: this client's first, then the all-clients ones. */
export function boxPhrasesFor(bp, contactId, box) {
    if (!bp)
        return [];
    return [...new Set([...(bp.clients[contactId]?.[box] ?? []), ...(bp.all[box] ?? [])])];
}
/**
 * V82: a phrase is for many notes, so it can't carry client details: the client's name, a date or a
 * phone number. True when it seems to.
 */
export function phraseHasClientInfo(text, names) {
    const t = text.toLowerCase();
    if (/\b\d{1,2}[/-]\d{1,2}([/-]\d{2,4})?\b/.test(t))
        return true;
    if (/\b\d{4}-\d{2}-\d{2}\b/.test(t))
        return true;
    if (/\(?\b\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/.test(t))
        return true;
    const words = new Set(t.split(/[^a-z'-]+/).filter(Boolean));
    return names
        .map((n) => n.trim().toLowerCase())
        .filter((n) => n.length > 1)
        .some((n) => words.has(n));
}
/** The fields a phrase can fill in (V81 DOT_FIELDS). */
export const DOT_FIELDS = [
    ['client', 'Client first name'],
    ['date', 'Session date'],
    ['length', 'Session length'],
    ['last plan', "Last session's plan"],
];
/** The practice's phrases until an admin changes them (V81 dotSeed). */
export const DEFAULT_DOT_PHRASES = [
    [
        'noshow',
        'Client did not attend the scheduled session. Outreach made by __ at __. Next appointment: __.',
    ],
    ['late', 'Client arrived __ minutes late; the session ran {length}.'],
    [
        'safety',
        'Safety assessed: client denied suicidal and homicidal ideation, intent and plan. Protective factors: __.',
    ],
    [
        'homework',
        'Reviewed homework from last session ({last plan}). {client} completed __ of the practice.',
    ],
    [
        'close',
        "Session ended with a review of today's work. {client} agreed to __ before the next session.",
    ],
    [
        'mse',
        'Client was alert and oriented x3, appearance appropriate, speech normal rate and volume, mood __, affect congruent, thought process logical.',
    ],
    [
        'tele',
        'Session held by video on {date}. Client confirmed a private location in __ and consented to telehealth.',
    ],
    [
        'cbt',
        'Clinician used cognitive restructuring to address __. {client} identified the thought __ and practiced an alternative.',
    ],
    [
        'progress',
        '{client} is making __ progress toward treatment goals. Weekly therapy remains necessary because __.',
    ],
    ['coord', 'Coordinated care with __ regarding __. Release of information on file.'],
].map(([name, text]) => ({ name: name, text: text }));
/** Stored phrases (JSON) as a clean list; anything malformed is left out. */
export function readDotPhrases(v) {
    if (!Array.isArray(v))
        return [];
    const out = [];
    for (const x of v) {
        const r = dotPhraseSchema.safeParse(x);
        if (r.success && !out.some((p) => p.name === r.data.name))
            out.push(r.data);
    }
    return out;
}
/**
 * ehr#437 (PROGRESS_NOTE_10-14-V81, 73 A to 76 A): "How do you want to write notes?", asked the
 * first time someone opens a note. Pick one or more: Talk it through (NotePilot on, it drafts when
 * you click Draft), Fill it in (every section open, phrase pills on each box), Review and sign
 * (NotePilot drafts the note when it opens with a summary). Saved per person in
 * users."notePrefs".writeStyle; an empty list means they closed the question without picking.
 */
export const WRITE_STYLES = ['talk', 'fill', 'review'];
export const WRITE_STYLE_TEXT = {
    talk: ['Talk it through', 'Dictate the session, then click Generate Notes.'],
    fill: ['Fill it in', 'Prompts and phrase pills in every box.'],
    review: ['Review and sign', 'The note drafts itself when you open it.'],
};
export const writeStyleSchema = z.object({
    style: z.array(z.enum(WRITE_STYLES)).max(3),
});
/** null: never asked. */
export function readWriteStyle(v) {
    if (!Array.isArray(v))
        return null;
    return WRITE_STYLES.filter((s) => v.includes(s));
}
/**
 * ehr#437 reopen (PROGRESS_NOTE_10-14-V81 Note settings, the cog): how the note works for this
 * person. Note mode is a bundle of the switches below (Guided, Standard, Expert; Custom when they
 * were changed one by one); AI drafting is when NotePilot drafts. Saved per person in
 * users."notePrefs".noteSettings. Not saved: they follow the writing style (picking a writing
 * style clears them, as V81's wsApply does).
 */
export const BOX_PROMPTS = ['example', 'hint', 'none'];
export const AI_DRAFT_MODES = ['open', 'click', 'off'];
export const AI_DRAFT_TEXT = {
    open: 'When I open the note',
    click: 'When I click Generate Notes',
    off: 'Off',
};
export const noteModeSchema = z.object({
    /** Box prompts: an example answer, a hint, or nothing. */
    prompt: z.enum(BOX_PROMPTS),
    /** Every section starts open. */
    openAll: z.boolean(),
    /** Phrase pills under each box. */
    pills: z.boolean(),
    /** Quick add and My changes start open in the rail. */
    quickOpen: z.boolean(),
    /** "N left to sign" in the sign-off bar. */
    whatsLeft: z.boolean(),
    /** The copied-text check (questions on text copied from an earlier note). */
    copyQs: z.boolean(),
    /**
     * ehr#437 Payor rules: "Billing checks on the note" (V81 inlineWarn). Settings saved before it
     * existed leave it out, which reads as on.
     */
    inlineWarn: z.boolean().optional(),
});
export const NOTE_MODES = ['guided', 'standard', 'expert'];
export const NOTE_MODE_TEXT = {
    guided: 'Guided',
    standard: 'Standard',
    expert: 'Expert',
    custom: 'Custom',
};
/** V81 MODE_BUNDLES. */
export const MODE_BUNDLES = {
    guided: {
        prompt: 'example',
        openAll: true,
        pills: true,
        quickOpen: true,
        whatsLeft: true,
        copyQs: true,
        inlineWarn: true,
    },
    standard: {
        prompt: 'hint',
        openAll: false,
        pills: true,
        quickOpen: false,
        whatsLeft: true,
        copyQs: true,
        inlineWarn: true,
    },
    expert: {
        prompt: 'none',
        openAll: false,
        pills: false,
        quickOpen: false,
        whatsLeft: true,
        copyQs: false,
        inlineWarn: false,
    },
};
/** The bundle these switches match, or custom. */
export function modeOf(o) {
    return (NOTE_MODES.find((m) => Object.keys(MODE_BUNDLES[m]).every(
    // a switch saved before it existed matches any bundle
    (k) => o[k] === undefined || MODE_BUNDLES[m][k] === o[k])) ?? 'custom');
}
export const noteSettingsSchema = z.object({
    mode: noteModeSchema,
    ai: z.enum(AI_DRAFT_MODES),
});
export function readNoteSettings(v) {
    const r = noteSettingsSchema.safeParse(v);
    return r.success ? r.data : null;
}
/**
 * V81 wsApply: Fill it in → Guided, else Talk it through → Standard, else Expert; AI drafts on
 * open with Review and sign, on Draft with Talk it through, and is off for Fill it in alone. Not
 * asked yet, or closed without a pick: Standard, drafting on Draft (the note as it always was).
 */
export function settingsFromStyle(style) {
    if (!style?.length)
        return { mode: { ...MODE_BUNDLES.standard }, ai: 'click' };
    const has = (s) => style.includes(s);
    return {
        mode: { ...MODE_BUNDLES[has('fill') ? 'guided' : has('talk') ? 'standard' : 'expert'] },
        ai: has('review') ? 'open' : has('talk') ? 'click' : 'off',
    };
}
/**
 * ehr#682 (PROGRESS-NOTE-10-20-V141 V120): no Note mode, Box prompts or switches any more. Every
 * note runs the old Standard bundle (hints inside the boxes) with every section open.
 */
export const V141_NOTE_MODE = { ...MODE_BUNDLES.standard, openAll: true };
/**
 * ehr#682 (V141 V126): what the note uses. "Generate notes with AI" is On or Off and On generates
 * the note when it opens: a saved Off stays Off; a saved "When I click Generate Notes", or nothing
 * saved, reads as On. The writing style and saved Note mode are no longer read.
 */
export function v141NoteSettings(saved) {
    return { mode: { ...V141_NOTE_MODE }, ai: saved?.ai === 'off' ? 'off' : 'open' };
}
//# sourceMappingURL=notePhrases.js.map